import { BASE_URL } from "../config";
import { getSessionId, clearSession } from "../utils/SessionHandler";

const base = `${BASE_URL.replace(/\/$/, "")}/api/v1/organisation/ai`;

async function checkedFetch(path, options = {}) {
  const token = getSessionId();
  if (!token) throw new Error("Sign in to use the ERP assistant");
  const response = await fetch(`${base}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.body && !(options.body instanceof FormData)
        ? { "Content-Type": "application/json" }
        : {}),
      ...options.headers,
    },
    cache: "no-store",
  });
  if (!response.ok) {
    let message = `ERP request failed (${response.status})`;
    let data = {};
    try {
      data = (await response.json()) || {};
      message = typeof data.message === "string" ? data.message :
        typeof data.description === "string" ? data.description : message;
    } catch (_) {}
    if (response.status === 404 && (path === "/status" || path === "/threads") &&
      (message === "ERP request failed (404)" || /^not found$/i.test(message))) {
      message = "The ERP AI service was not found on the configured API server (404). Ask your administrator to deploy the AI-enabled ERP backend and verify the website's API URL.";
    }
    // Older ERP servers wrap authentication failures in an HTTP 400 response.
    if (response.status === 401 || data.http_status === 401) {
      clearSession();
      window.dispatchEvent(new Event("erp-session-expired"));
      message = "Your ERP session has expired or is invalid. Sign in again to use the assistant.";
    }
    const error = new Error(message);
    error.status = response.status;
    error.path = path;
    throw error;
  }
  return response;
}

export async function aiRequest(path, options = {}) {
  const response = await checkedFetch(path, options);
  return response.status === 204 ? null : response.json();
}

export function createEventParser(onEvent) {
  let pending = "";
  const process = (block) => {
    let type = "message";
    const data = [];
    block.split(/\r?\n/).forEach((line) => {
      if (line.startsWith("event:")) type = line.slice(6).trim();
      if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
    });
    if (data.length) onEvent(type, JSON.parse(data.join("\n")));
  };
  return {
    push(chunk) {
      pending += chunk;
      if (pending.length > 8 * 1024 * 1024) throw new Error("Assistant event exceeded its size limit");
      let boundary;
      while ((boundary = /\r?\n\r?\n/.exec(pending))) {
        process(pending.slice(0, boundary.index));
        pending = pending.slice(boundary.index + boundary[0].length);
      }
    },
    finish() {
      if (pending.trim()) process(pending);
      pending = "";
    },
  };
}

export async function aiStream(input, onEvent, signal) {
  const response = await checkedFetch("/turns/stream", {
    method: "POST",
    body: JSON.stringify(input),
    headers: { Accept: "text/event-stream" },
    signal,
  });
  if (!response.body || !response.headers.get("content-type")?.includes("text/event-stream")) {
    throw new Error("The ERP server did not return an assistant stream");
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let settled = null;
  let complete = false;
  const parser = createEventParser((type, data) => {
    if (type === "error") throw new Error(data.message || "Assistant request failed");
    if (type === "done" || type === "settled") settled = data;
    onEvent(type, data);
  });
  try {
    while (true) {
      const chunk = await reader.read();
      if (chunk.done) { complete = true; break; }
      parser.push(decoder.decode(chunk.value, { stream: true }));
    }
    parser.push(decoder.decode());
    parser.finish();
    if (!settled) throw new Error("Connection interrupted. Reload the conversation before retrying.");
    return settled;
  } finally {
    if (!complete) await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}

export async function downloadReport(report) {
  const response = await checkedFetch(`/reports/${encodeURIComponent(report.id)}/content`);
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `erp-report-${report.id}.${report.format}`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const newRequestId = () => crypto.randomUUID();